import Script from 'next/script';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import useRequestContext from '../../contexts/RequestContext';
import useIsAdmin from '../../hooks/useIsAdmin';
import { useUserPrivacyConsent } from '../../redux/user/userHooks';
import { boxModelStyles, IBoxModelStyleProps } from '../../styles/css';
import StyledCard from '../../ui/Card/StyledCard';
import LoadingSpinner from '../../ui/LoadingSpinner/LoadingSpinner';

declare global {
  interface Window {
    _taboola: { push: (...rest: any) => void };
  }
}

const StyledCardExtended = styled(StyledCard)`
  width: auto;
  max-width: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 120px;

  ${boxModelStyles}
`;

const PREFIX = 'Alternating-Thumbnails-';

export const ContentPartnerMode = {
  layout_1x4: `${PREFIX}A`,
  layout_4x2: `${PREFIX}B`,
  layout_3x2: `${PREFIX}C`,
  layout_2x2: `${PREFIX}D`,
  layout_responsive_dark: `${PREFIX}E`
} as const;

export enum ContentPartnerType {
  article = 'article',
  category = 'category',
  home = 'home',
  other = 'other',
  photo = 'photo',
  search = 'search'
}

const typeToValue = type => {
  if (type === ContentPartnerType.category) return 'same';
  return 'auto;';
};

const ELEMENT_ID = `content_partner_ad`; //taboola_${Math.floor(Math.random() * 100000)}`;
const LOAD_TABOOLA_DELAY = 4000;
const TABOOLA_LIBRARY_SRC =
  '//cdn.taboola.com/libtrc/cbsinteractive-maxpreps/loader.js';

let loadCount = 0;

export interface IContentPartnerProps extends IBoxModelStyleProps {
  mode: typeof ContentPartnerMode[keyof typeof ContentPartnerMode];
  type?: ContentPartnerType;
  placement?: string;
}

// This control works using the concept that each page is going to
// create a new instance of this component (as opposed to the same instance getting updated props)
const ContentPartner: React.FC<IContentPartnerProps> = ({
  mode,
  type = ContentPartnerType.other,
  placement = 'React',
  ...rest
}) => {
  const isAdmin = useIsAdmin();
  const consent = useUserPrivacyConsent();
  const { appType } = useRequestContext();

  const [isLoadScriptTimeoutComplete, setIsLoadScriptTimeoutComplete] =
    useState<boolean>(false);

  // if we have more rules over time then we should add them here
  const isEnabled = !isAdmin && !appType;

  const requestAd = useCallback<(isReload: boolean) => void>(
    (isReload = false) => {
      loadCount += 1;

      if (!isEnabled || !consent.targeting) {
        return;
      }

      // eslint-disable-next-line no-multi-assign
      const taboola = (window._taboola = window._taboola || []);

      if (isReload) {
        taboola.push({ notify: 'newPageLoad' });
      }

      const data = [
        {
          [type]: typeToValue(type),
          url: window.location.pathname
        },
        {
          mode: mode.toLowerCase(),
          container: ELEMENT_ID,
          // Ex. of WF format
          // placement: 'Desktop Teams Bottom Page Thumbnails',
          placement,
          target_type: 'mix'
        },
        {
          flush: 'true'
        }
      ];

      taboola.push(...data);

      console.log(
        'Content Partner',
        'Request Ad',
        data,
        'Load count: ' + loadCount
      );
    },
    [mode, type, isEnabled, consent.targeting, placement]
  );

  // on 2nd+ page
  useEffect(() => {
    if (loadCount > 0) {
      setTimeout(() => {
        requestAd(true);
      }, LOAD_TABOOLA_DELAY / 2);
    }
  }, [requestAd]);

  // handles loading the script after a delay
  useEffect(() => {
    setTimeout(() => {
      setIsLoadScriptTimeoutComplete(true);
    }, LOAD_TABOOLA_DELAY);
  }, []);

  // if not enabled don't load the script or element
  if (!isEnabled) {
    return null;
  }

  return (
    <>
      {isLoadScriptTimeoutComplete && (
        <Script
          strategy="lazyOnload"
          id={TABOOLA_LIBRARY_SRC}
          src={TABOOLA_LIBRARY_SRC}
          onLoad={requestAd}
        />
      )}
      <StyledCardExtended id={ELEMENT_ID} {...rest}>
        <LoadingSpinner center />
      </StyledCardExtended>
    </>
  );
};

export default ContentPartner;
